import { Button, Flex, Input, Portal } from '@chakra-ui/react';
import { autoUpdate, flip, size, useFloating } from '@floating-ui/react';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';

import { toaster } from '@components/ui/toaster';
import AddToCartPopover from '@features/marketplace/catalog/AddToCartPopover';
import { useOverridesContext } from '@features/marketplace/catalog/context';
import { ProductDetails } from 'helpers/fakeData';
import { useBoolean } from 'hooks/useBoolean';
import { useStores } from 'stores/rootStore';

interface Props {
  product: ProductDetails;
}

const AddToCart = ({ product }: Props) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<ProductDetails>(product);
  const [, setOverrides] = useOverridesContext();

  const { marketPlaceStore } = useStores();
  const cartItem = marketPlaceStore.cart.find((item) => item.id === product.id);
  const [isAdding, setIsAdding] = useBoolean();
  const { refs, floatingStyles } = useFloating({
    open: isAdding,
    onOpenChange: setIsAdding.toggle,
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            marginTop: '8px',
          });
        },
      }),
      flip(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const handleSelectVariant = useCallback(
    (variant: ProductDetails) => {
      setSelectedVariant(variant);
      setOverrides((prev) => ({ ...prev, product: variant }));
    },
    [setOverrides],
  );

  const onQuantityChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setQuantity(value);
  }, []);

  const onCancel = useCallback(() => {
    setIsAdding.off();
    setOverrides((prev) => ({ ...prev, product: null }));
  }, [setIsAdding, setOverrides]);

  const addToCart = useCallback(() => {
    const existingQuantity = cartItem?.quantity ?? 0;
    const finalQuantity = quantity + existingQuantity;

    marketPlaceStore.addToCart({
      id: selectedVariant.id,
      quantity: finalQuantity,
    });
    toaster.create({
      title: selectedVariant.name,
      description: `Added to cart`,
      type: 'success',
    });
    setQuantity(1);
    onCancel();
  }, [marketPlaceStore, quantity, setQuantity, cartItem, selectedVariant, onCancel]);

  const hasVariants = product.variants && product.variants.length > 1;

  const handleOpenAddToCartPopover = useCallback(() => {
    setIsAdding.on();
  }, [setIsAdding]);

  return (
    <Flex flexDirection="row" gap={2}>
      <Input
        borderColor="border.light"
        type="number"
        min={1}
        value={quantity}
        w="60px"
        size="xs"
        onChange={onQuantityChange}
      />
      {!hasVariants ? (
        <Button size="xs" variant="solid" onClick={addToCart}>
          Add to cart
        </Button>
      ) : (
        <>
          <Button
            ref={refs.setReference}
            size="xs"
            variant="solid"
            onClick={handleOpenAddToCartPopover}
            disabled={isAdding}
          >
            {isAdding ? 'Adding...' : 'Add to cart'}
          </Button>
          {isAdding && (
            <Portal>
              <AddToCartPopover
                product={product}
                onSelectVariant={handleSelectVariant}
                ref={refs.floating}
                setFloatingReference={refs.setFloating}
                floatingStyles={floatingStyles}
                onCancel={onCancel}
                onClickAddToCart={addToCart}
              />
            </Portal>
          )}
        </>
      )}
    </Flex>
  );
};

export default observer(AddToCart);
